import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { InputSection } from '@/components/InputSection';
import { OneShotInputSection, OneShotSubmitParams } from '@/components/OneShotInputSection';
import { VideoPlayer } from '@/components/VideoPlayer';
import { NotesSection } from '@/components/NotesSection';
import { ChatSection } from '@/components/ChatSection';
import { AboutPage } from '@/components/AboutPage';
import { PrivacyPolicy } from '@/components/PrivacyPolicy';
import { TermsOfService } from '@/components/TermsOfService';
import { TopicsPage } from '@/components/TopicsPage';
import { QuestionPage } from '@/components/QuestionPage';
import { ExplorePage } from '@/components/ExplorePage';
import { BlogPage } from '@/components/BlogPage';
import { ToolsPage } from '@/components/ToolsPage';
import { QuotaCounter } from '@/components/QuotaCounter';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { MyVideos } from '@/components/MyVideos';
import { RenderVideoPage } from '@/components/RenderVideoPage';
import { TransitionVideo } from '@/components/TransitionVideo';
import { GoldenParticlesBackground } from '@/components/GoldenParticlesBackground';
import { triggerRenderAndYouTubeUpload } from '@/lib/renderClient';
import YouTubeHub from '@/components/YouTubeHub';
import { generateSolution, AIOutput } from '@/lib/gemini';
import { generateOneShotSolution } from '@/lib/oneshot';
import { fetchStaticImage } from '@/lib/imageService';
import { isValidRenderableImageUrl } from '@/lib/visualUtils';
import { GraduationCap, Sparkles, Youtube, BookOpen, Settings2, X, HelpCircle, CheckSquare, AlertCircle, ShieldAlert, CheckCircle2, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

declare global {
  interface Window {
    aistudio?: {
      hasSelectedApiKey: () => Promise<boolean>;
      openSelectKey: () => Promise<void>;
    };
  }
}

export default function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiData, setAiData] = useState<AIOutput | null>(null);
  const [doubtData, setDoubtData] = useState<AIOutput | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("English");
  const [activeMode, setActiveMode] = useState<'doubt' | 'oneshot'>('doubt');

  // Scaling State
  const [scale, setScale] = useState(1);
  const BASE_WIDTH = 1920;
  const BASE_HEIGHT = 960; // 18:9 Ratio (2:1)

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const scaleX = windowWidth / BASE_WIDTH;
      const scaleY = windowHeight / BASE_HEIGHT;
      const newScale = Math.min(scaleX, scaleY);
      setScale(newScale);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Overlay Adjustment State
  const [overlayX, setOverlayX] = useState(0);
  const [overlayY, setOverlayY] = useState(-153);
  const [overlayScale, setOverlayScale] = useState(0.50);
  const [overlayOpacity, setOverlayOpacity] = useState(1);

  // Intro Animation State
  const [showIntro, setShowIntro] = useState(true);
  const [showAbout, setShowAbout] = useState(false);
  const [isFinishingTransition, setIsFinishingTransition] = useState(false);

  // API Key Modal State
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [isAuthInstructionsModalOpen, setIsAuthInstructionsModalOpen] = useState(false);
  const [userApiKey, setUserApiKey] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(() => localStorage.getItem('google_user_email'));
  const [isYtConnected, setIsYtConnected] = useState(true);
  const [useSimulation, setUseSimulation] = useState(true);

  // Check YouTube connection status
  const checkYtConnection = () => {
    // Skip status checks when page is running inside a Puppeteer render instance
    if (typeof window !== 'undefined' && window.location.search.includes('id=')) {
      return;
    }
    fetch('/api/youtube/connected')
      .then(res => {
        if (!res.ok) return null;
        return res.json();
      })
      .then(data => {
        if (!data) return;
        setIsYtConnected(!!data.connected);
        if (typeof data.useSimulation === "boolean") {
          setUseSimulation(data.useSimulation);
        }
      })
      .catch(err => {
        // Silently ignore transient network / rate-limit check errors
      });
  };

  const toggleSimulation = () => {
    const nextVal = !useSimulation;
    setUseSimulation(nextVal);
    fetch("/api/youtube/config", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ useSimulation: nextVal })
    })
      .then(res => {
        if (!res.ok) return null;
        return res.json();
      })
      .then(data => {
        if (data && data.success) {
          setUseSimulation(data.config.useSimulation);
          checkYtConnection();
        }
      })
      .catch(err => console.error("Error toggling simulation config:", err));
  };

  useEffect(() => {
    (window as any).checkYtConnection = checkYtConnection;
    checkYtConnection();
    // Poll YouTube connection status every 45 seconds instead of 8 seconds to prevent rate limits
    const interval = setInterval(checkYtConnection, 45000);
    return () => {
      clearInterval(interval);
      delete (window as any).checkYtConnection;
    };
  }, [userEmail]);

  // Simple routing state
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = (path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    // Scroll to top on navigation
    const scrollContainer = document.getElementById('scroll-container');
    if (scrollContainer) scrollContainer.scrollTo(0, 0);
  };

  const handleContactClick = () => {
    // If we're on privacy or terms, go back to home first
    if (currentPath !== '/') {
      navigateTo('/');
    }
    
    setShowAbout(true);
    
    // Small delay to allow AboutPage to mount/state to update and reach the very bottom
    setTimeout(() => {
      const scrollContainer = document.getElementById('scroll-container');
      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 250);
  };

  // Load saved adjustments
  useEffect(() => {
    const saved = localStorage.getItem('overlay_adjustments');
    if (saved) {
      const parsed = JSON.parse(saved);
      setOverlayX(parsed.x !== undefined ? parsed.x : 0);
      setOverlayY(parsed.y !== undefined ? parsed.y : -153);
      setOverlayScale(parsed.scale !== undefined ? parsed.scale : 0.50);
      setOverlayOpacity(parsed.opacity !== undefined ? parsed.opacity : 1);
    } else {
      // Force defaults if nothing is saved
      setOverlayX(0);
      setOverlayY(-153);
      setOverlayScale(0.50);
      setOverlayOpacity(1);
    }

    const savedKey = localStorage.getItem('gemini_api_key');
    if (savedKey) setUserApiKey(savedKey);

    const savedEmail = localStorage.getItem('google_user_email');
    if (savedEmail) {
      setUserEmail(savedEmail);
    } else {
      setUserEmail(null);
    }
  }, []);

  // OAuth logic & session management
  useEffect(() => {
    // Check active server session on load
    fetch('/api/auth/session', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.authenticated && data.user?.email) {
          setUserEmail(data.user.email);
          localStorage.setItem('google_user_email', data.user.email);
          if (data.accessToken) {
            localStorage.setItem('google_access_token', data.accessToken);
          }
        }
      })
      .catch(() => {});

    const handleAuthMessage = (event: MessageEvent) => {
      if (event.data?.type === 'GOOGLE_AUTH_SUCCESS' || event.data?.type === 'OAUTH_AUTH_SUCCESS') {
        const { email, sessionId, accessToken, name } = event.data.data || {};
        if (email) {
          localStorage.setItem('google_user_email', email);
          setUserEmail(email);
          // Ping login reminder API to trigger welcome email (protected by 24h deduplication)
          fetch('/api/auth/login-reminder', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, name })
          }).catch(() => {});
        }
        if (sessionId) {
          localStorage.setItem('google_session_id', sessionId);
        }
        if (accessToken) {
          localStorage.setItem('google_access_token', accessToken);
        }
      } else if (event.data?.type === 'YOUTUBE_AUTH_SUCCESS') {
        checkYtConnection();
      }
    };

    window.addEventListener('message', handleAuthMessage);
    return () => window.removeEventListener('message', handleAuthMessage);
  }, []);

  const logoutFromGoogle = () => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    localStorage.removeItem('google_access_token');
    localStorage.removeItem('google_user_email');
    localStorage.removeItem('google_session_id');
    setUserEmail(null);
  };

  const loginWithGoogle = () => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const resp = await fetch(`/api/auth/url?origin=${encodeURIComponent(window.location.origin)}`);
        const { url } = await resp.json();
        const width = 500;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        const popup = window.open(url, 'google-login', `width=${width},height=${height},left=${left},top=${top}`);

        const handleAuthMessage = (event: MessageEvent) => {
          if (event.data?.type === 'GOOGLE_AUTH_SUCCESS' || event.data?.type === 'OAUTH_AUTH_SUCCESS') {
            window.removeEventListener('message', handleAuthMessage);
            resolve();
          } else if (event.data?.type === 'GOOGLE_AUTH_CANCELLED') {
            window.removeEventListener('message', handleAuthMessage);
            resolve();
          }
        };
        window.addEventListener('message', handleAuthMessage);

        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleAuthMessage);
            resolve();
          }
        }, 500);

        // Keep the success listener resolving our promise
      } catch (err) {
        console.error("Login URL fetch failed:", err);
        setError("Failed to start Google login. Please ensure OAuth credentials are set.");
        reject(err);
      }
    });
  };

  const cleanAuthErrorMessage = (errorText: string | null | undefined): string | null => {
    if (!errorText) return null;
    const lower = errorText.toLowerCase();
    if (
      lower.includes("invalid authentication credentials") ||
      lower.includes("expected oauth 2 access token") ||
      lower.includes("401")
    ) {
      return "Session expired or authentication failed. Please log out and sign in again, or switch to a different Google account.";
    }
    return errorText;
  };

  const handleProceedGoogleLogin = async () => {
    setIsAuthInstructionsModalOpen(false);
    try {
      await loginWithGoogle();
    } catch (err: any) {
      console.error("Popup login failed:", err);
      if (err.message !== "Login window was closed") {
        setError(cleanAuthErrorMessage(err.message) || "Failed to log in with Google.");
      }
    }
  };

  // Intro timer
  useEffect(() => {
    if (!aiData) {
      setShowIntro(true);
      const timer = setTimeout(() => {
        setShowIntro(false);
      }, 3000); // 3 seconds
      return () => clearTimeout(timer);
    }
  }, [aiData]);

  // Save adjustments
  useEffect(() => {
    localStorage.setItem('overlay_adjustments', JSON.stringify({
      x: overlayX,
      y: overlayY,
      scale: overlayScale,
      opacity: overlayOpacity
    }));
  }, [overlayX, overlayY, overlayScale, overlayOpacity]);

  const [activeSection, setActiveSection] = useState('hero');

  // Scroll to top when aiData is set
  useEffect(() => {
    if (aiData) {
      // Use requestAnimationFrame to ensure the scroll happens after the DOM has updated
      requestAnimationFrame(() => {
        const scrollContainer = document.getElementById('scroll-container');
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
          // Also try scrolling the window just in case
          window.scrollTo(0, 0);
        }
      });
    }
  }, [aiData]);

  // Track active section for sidebar
  useEffect(() => {
    const scrollContainer = document.getElementById('scroll-container');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const sections = aiData 
        ? ['video-section', 'results-details']
        : ['hero-section', 'input-section', 'features-section'];
      
      let current = sections[0];
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Adjust threshold for activation
          if (rect.top <= 300) {
            current = sectionId;
          }
        }
      }
      setActiveSection(current);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [aiData]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    const scrollContainer = document.getElementById('scroll-container');
    if (element && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const relativeTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
      
      scrollContainer.scrollTo({
        top: relativeTop - 40, // Offset for padding
        behavior: 'smooth'
      });
    }
  };

  const handleReplayVideo = (videoData: AIOutput, lang: string) => {
    navigateTo('/');
    setAiData(videoData);
    if (lang) {
      setSelectedLanguage(lang);
    }
    setDoubtData(null);
    setTimeout(() => {
      scrollToSection('video-section');
    }, 100);
  };

  const handleGenerate = async (text: string, imageBase64?: string, mimeType?: string, language?: string) => {
    if (isLoading) return;
    setError(null);
    
    // Auth Check Logic
    const hasKey = !!localStorage.getItem('gemini_api_key');
    const hasToken = !!localStorage.getItem('google_access_token') || !!localStorage.getItem('google_session_id') || !!userEmail;

    if (!hasKey && !hasToken) {
      // Case A: Not logged in & No API Key -> Trigger Google Login Instructions
      setIsAuthInstructionsModalOpen(true);
      return;
    }

    // Step 1: Fade out Professor X image
    setIsFinishingTransition(true);
    
    // Step 2: Wait for image fade animation to complete (0.8s as per its exit duration)
    // plus a small buffer for smoother feel
    await new Promise(resolve => setTimeout(resolve, 900));

    setIsLoading(true);
    setDoubtData(null); // Clear previous doubt when a new question is asked
    if (language) {
      setSelectedLanguage(language);
    }
    try {
      const data = await generateSolution(text, imageBase64, mimeType, language);
      if (!data || !data.steps || !Array.isArray(data.steps) || data.steps.length === 0) {
        throw new Error("The AI returned an invalid or empty solution format. Please try again.");
      }
      
      const staticImageUrl = isValidRenderableImageUrl(data.static_image_url) ? data.static_image_url : undefined;

      // Sanitize data to prevent React rendering crashes and reject invalid/broken PDF visuals
      const sanitizedData: AIOutput = {
        subject: typeof data.subject === 'string' ? data.subject : 'General',
        topic: typeof data.topic === 'string' ? data.topic : 'Math',
        question: typeof data.question === 'string' ? data.question : 'N/A',
        steps: data.steps.map((step: any, index: number) => {
          const stepRawImg = step && (step.image_url || step.static_image_url);
          const validStepImg = isValidRenderableImageUrl(stepRawImg) ? stepRawImg : undefined;

          return {
            step: step && typeof step.step === 'number' ? step.step : index + 1,
            board_content: step && typeof step.board_content === 'string' ? step.board_content : '',
            explanation: step && typeof step.explanation === 'string' ? step.explanation : '',
            audio_explanation: step && typeof step.audio_explanation === 'string' ? step.audio_explanation : undefined,
            reference_text: step && typeof step.reference_text === 'string' ? step.reference_text : undefined,
            diagram_svg: step && typeof step.diagram_svg === 'string' ? step.diagram_svg : undefined,
            diagram_description: step && typeof step.diagram_description === 'string' ? step.diagram_description : undefined,
            image_url: validStepImg,
            image_query: step && typeof step.image_query === 'string' ? step.image_query : undefined,
            static_image_url: validStepImg,
          };
        }),
        formulas: Array.isArray(data.formulas) ? data.formulas.filter(f => typeof f === 'string') : [],
        diagram_needed: !!data.diagram_needed || !!staticImageUrl || data.steps.some((s: any) => !!s.diagram_svg || isValidRenderableImageUrl(s.image_url) || isValidRenderableImageUrl(s.static_image_url)),
        visual_type: data.visual_type || (staticImageUrl ? 'STATIC' : 'NONE'),
        static_image_url: staticImageUrl,
        static_image_query: typeof data.static_image_query === 'string' ? data.static_image_query : undefined,
        image_keywords: Array.isArray(data.image_keywords) ? data.image_keywords : undefined,
        diagram_description: typeof data.diagram_description === 'string' ? data.diagram_description : undefined,
        final_answer: typeof data.final_answer === 'string' ? data.final_answer : undefined,
      };

      setAiData(sanitizedData);
      
      const effectiveEmail = userEmail || '';
      try {
        const storageKey = effectiveEmail ? `profx_saved_videos_${effectiveEmail}` : 'profx_saved_videos_guest';
        const existingRaw = localStorage.getItem(storageKey);
        const existingVideos = existingRaw ? JSON.parse(existingRaw) : [];
        
        const newVideo = {
          id: Math.random().toString(36).substring(2, 9) + '_' + Date.now(),
          title: sanitizedData.topic || sanitizedData.subject || "AI Explanation",
          question: text || sanitizedData.question || "Visual Explanation",
          subject: sanitizedData.subject || "General",
          topic: sanitizedData.topic || "Explanation",
          language: selectedLanguage || "English",
          timestamp: Date.now(),
          videoData: sanitizedData,
          imageUrl: sanitizedData.static_image_url || undefined,
        };
        
        const isDuplicate = existingVideos.some((v: any) => v.question?.trim().toLowerCase() === newVideo.question?.trim().toLowerCase());
        
        if (!isDuplicate) {
          existingVideos.unshift(newVideo);
          localStorage.setItem(storageKey, JSON.stringify(existingVideos));
          window.dispatchEvent(new Event('profx_videos_updated'));

          // Dispatch universal background rendering & YouTube auto-upload
          triggerRenderAndYouTubeUpload({
            id: newVideo.id,
            metadata: {
              question: newVideo.question,
              subject: newVideo.subject,
              topic: newVideo.topic,
              language: newVideo.language,
              timestamp: newVideo.timestamp,
              email: userEmail || undefined
            },
            videoData: newVideo.videoData
          });
        }
      } catch (saveErr) {
        console.error("Failed to save generated video inside handleGenerate:", saveErr);
      }

      if (userEmail) {
        const today = new Date().toDateString();
        const savedDate = localStorage.getItem(`quota_date_${userEmail}`);
        let savedUsage = 0;
        
        if (savedDate === today) {
            savedUsage = parseInt(localStorage.getItem(`quota_${userEmail}`) || '0');
        } else {
            localStorage.setItem(`quota_date_${userEmail}`, today);
        }
        
        const increment = Math.floor(Math.random() * 8) + 3; // Mock 3-10% usage increment per generation
        const newUsage = Math.min(100, savedUsage + increment); 
        localStorage.setItem(`quota_${userEmail}`, newUsage.toString());
        window.dispatchEvent(new Event('quota_updated'));
      }

    } catch (err: any) {
      console.error("Failed to generate solution:", err);
      const hasUserKey = !!localStorage.getItem('gemini_api_key');
      const errLower = (err.message || "").toLowerCase();
      const isAuthCredentialError = 
        errLower.includes("invalid authentication credentials") || 
        errLower.includes("expected oauth 2 access token") || 
        err.message?.includes("401") ||
        err.status === 401 ||
        err.statusCode === 401;
      const isScopeError = errLower.includes("scope") || errLower.includes("insufficient") || errLower.includes("permission");
      const isHighDemand = err.message?.includes("currently experiencing high demand") || 
                           err.message?.includes("UNAVAILABLE") || 
                           err.message?.includes("503") || 
                           err.message?.includes("currently busy");

      if (isAuthCredentialError) {
        setError("Session expired or authentication failed. Please log out and sign in again, or switch to a different Google account.");
      } else if (isHighDemand) {
        setError("our server is currently busy try soon");
      } else if (isScopeError) {
        setError("Request had insufficient authentication scopes. To resolve this error and get the best result, please log out, sign in with Google again, and make sure you completely TICK/SELECT ALL permission options (or check the 'Select all' checkbox) on the Google screen.");
      } else if (err.message?.includes("429") || err.message?.includes("quota") || err.message?.includes("RESOURCE_EXHAUSTED")) {
        if (userEmail && !hasUserKey) {
            const today = new Date().toDateString();
            localStorage.setItem(`quota_date_${userEmail}`, today);
            localStorage.setItem(`quota_${userEmail}`, '100');
            window.dispatchEvent(new Event('quota_updated'));
        }
        if (hasUserKey) {
          setError("Your personal API key has exceeded its quota. Please check your Google AI Studio billing or usage limits, or try again later.");
        } else {
          setError("API Quota Exceeded. Please click the 'Set API Key' button in the top right to provide your own Gemini API key for unlimited use.");
        }
      } else {
        setError(cleanAuthErrorMessage(err.message) || "An unexpected error occurred while generating the solution.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOneShotSubmit = async (params: OneShotSubmitParams) => {
    if (isLoading) return;
    setError(null);
    const hasKey = !!localStorage.getItem('gemini_api_key');
    const hasToken = !!localStorage.getItem('google_access_token') || !!localStorage.getItem('google_session_id') || !!userEmail;

    if (!hasKey && !hasToken) {
      setIsAuthInstructionsModalOpen(true);
      return;
    }

    setIsFinishingTransition(true);
    await new Promise(resolve => setTimeout(resolve, 900));

    setIsLoading(true);
    setDoubtData(null);
    if (params.language) {
      setSelectedLanguage(params.language);
    }

    try {
      const data = await generateOneShotSolution(
        params.subject,
        params.classLevel,
        params.board,
        params.chapterTopic,
        params.language,
        params.studyMaterialsContext
      );

      if (!data || !data.steps || !Array.isArray(data.steps) || data.steps.length === 0) {
        throw new Error("The OneShot generator returned an empty solution format. Please try again.");
      }

      setAiData(data);

      const effectiveEmail = userEmail || '';
      try {
        const storageKey = effectiveEmail ? `profx_saved_videos_${effectiveEmail}` : 'profx_saved_videos_guest';
        const existingRaw = localStorage.getItem(storageKey);
        const existingVideos = existingRaw ? JSON.parse(existingRaw) : [];

        const newVideo = {
          id: Math.random().toString(36).substring(2, 9) + '_' + Date.now(),
          title: `[OneShot] ${data.topic || params.chapterTopic}`,
          question: `OneShot Masterclass: ${params.chapterTopic}`,
          subject: data.subject || params.subject,
          topic: data.topic || params.chapterTopic,
          language: params.language || "English",
          timestamp: Date.now(),
          videoData: data,
          imageUrl: data.static_image_url || undefined,
          mode: 'oneshot'
        };

        const isDuplicate = existingVideos.some((v: any) => v.question?.trim().toLowerCase() === newVideo.question?.trim().toLowerCase());

        if (!isDuplicate) {
          existingVideos.unshift(newVideo);
          localStorage.setItem(storageKey, JSON.stringify(existingVideos));
          window.dispatchEvent(new Event('profx_videos_updated'));

          // Dispatch universal background rendering & YouTube auto-upload
          triggerRenderAndYouTubeUpload({
            id: newVideo.id,
            metadata: {
              question: newVideo.question,
              subject: newVideo.subject,
              topic: newVideo.topic,
              language: newVideo.language,
              timestamp: newVideo.timestamp,
              email: userEmail || undefined
            },
            videoData: newVideo.videoData
          });
        }
      } catch (saveErr) {
        console.error("Failed to save generated OneShot video:", saveErr);
      }
    } catch (err: any) {
      console.error("Failed to generate OneShot solution:", err);
      const errLower = (err.message || "").toLowerCase();
      const isAuthCredentialError = 
        errLower.includes("invalid authentication credentials") || 
        errLower.includes("expected oauth 2 access token") || 
        err.message?.includes("401") ||
        err.status === 401 ||
        err.statusCode === 401;
      if (isAuthCredentialError) {
        setError("Session expired or authentication failed. Please log out and sign in again, or switch to a different Google account.");
      } else {
        setError(cleanAuthErrorMessage(err.message) || "An unexpected error occurred during OneShot Learning generation.");
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  if (currentPath.startsWith('/render-video')) {
    return <RenderVideoPage path={currentPath} />;
  }

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden">
      {/* Scaling Wrapper */}
      <div 
        className="relative bg-black text-slate-100 font-sans selection:bg-purple-500/30 overflow-hidden shadow-2xl"
        style={{ 
          width: `${BASE_WIDTH}px`, 
          height: `${BASE_HEIGHT}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          flexShrink: 0
        }}
      >
        {/* Dark Black Background with Dynamic Moving Golden Particles */}
        <GoldenParticlesBackground />

        {/* Sidebar Navigation */}
        <div className="absolute left-10 top-1/2 -translate-y-1/2 z-[100] flex flex-col gap-6">
          {(aiData 
            ? [
                { id: 'video-section', label: 'Video', icon: Youtube },
                { id: 'results-details', label: 'Notes & Chat', icon: BookOpen }
              ]
            : [
                { id: 'hero-section', label: 'Welcome', icon: Sparkles },
                { id: 'input-section', label: 'Ask Professor', icon: GraduationCap },
                { id: 'features-section', label: 'Features', icon: Settings2 }
              ]
          ).map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="group relative flex items-center"
            >
              <div className={cn(
                "w-3 h-3 rounded-full transition-all duration-300 border-2",
                activeSection === item.id 
                  ? "bg-indigo-500 border-indigo-500 scale-125 shadow-[0_0_10px_rgba(99,102,241,0.8)]" 
                  : "bg-transparent border-slate-600 group-hover:border-slate-400"
              )} />
              <span className={cn(
                "absolute left-8 px-3 py-1.5 rounded-lg bg-slate-900/90 backdrop-blur-md border border-white/10 text-xs font-bold text-white whitespace-nowrap opacity-0 -translate-x-2 transition-all duration-300 pointer-events-none",
                "group-hover:opacity-100 group-hover:translate-x-0"
              )}>
                {item.label}
              </span>
            </button>
          ))}
        </div>

        {/* Scrollable Content Container */}
        <div 
          id="scroll-container"
          className="absolute inset-0 z-[60] overflow-y-auto overflow-x-hidden scroll-smooth custom-scrollbar"
        >
          {/* Top Overlay Layer (8.png) moved inside for precise layering */}
          <AnimatePresence>
            {!aiData && !showIntro && !isLoading && !showAbout && !isFinishingTransition && currentPath === '/' && (
              <motion.img 
                key="overlay-8"
                src="/8.png"
                alt="Overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: overlayOpacity }}
                exit={{ 
                  opacity: 0, 
                  scale: overlayScale * 0.9,
                  filter: "blur(20px)",
                  transition: { duration: 0.8, ease: "easeIn" }
                }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="absolute top-0 left-0 w-full h-full object-contain z-[65] pointer-events-none"
                style={{ 
                  transform: `translate(${overlayX}px, ${overlayY}px) scale(${overlayScale})`,
                }}
              />
            )}
          </AnimatePresence>

          {/* Header */}
          {!aiData && currentPath === '/' && (
            <header className="relative z-40 bg-transparent">
              <div className="max-w-[1700px] mx-auto px-14 sm:px-16 h-22 flex items-center justify-between relative">
                <div className="flex items-center gap-4 cursor-pointer" onClick={() => setShowAbout(false)}>
                  <div className="bg-indigo-600 p-3 rounded-2xl shadow-inner">
                    <GraduationCap className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="text-3xl font-bold tracking-tight text-slate-100">Professor X</h1>
                </div>

                {/* Center Navigation */}
                <div className="absolute left-1/2 -translate-x-1/2 hidden md:flex items-center gap-8">
                  <button 
                    onClick={() => { setShowAbout(false); navigateTo('/topics'); }}
                    className={cn(
                      "font-bold transition-all text-base",
                      currentPath === '/topics' ? "text-indigo-400 drop-shadow-[0_0_10px_rgba(129,140,248,0.5)]" : "text-slate-300 hover:text-white"
                    )}
                  >
                    Topics
                  </button>
                  <button 
                    onClick={() => { setShowAbout(false); navigateTo('/tools'); }}
                    className={cn(
                      "font-bold transition-all text-base",
                      currentPath.startsWith('/tools') ? "text-indigo-400 drop-shadow-[0_0_10px_rgba(129,140,248,0.5)]" : "text-slate-300 hover:text-white"
                    )}
                  >
                    Tools
                  </button>
                  <button 
                    onClick={() => { setShowAbout(false); navigateTo('/blog'); }}
                    className={cn(
                      "font-bold transition-all text-base",
                      currentPath.startsWith('/blog') ? "text-indigo-400 drop-shadow-[0_0_10px_rgba(129,140,248,0.5)]" : "text-slate-300 hover:text-white"
                    )}
                  >
                    Blogs
                  </button>
                  <button 
                    onClick={() => { setShowAbout(false); navigateTo('/my-videos'); }}
                    className={cn(
                      "font-bold transition-all text-base",
                      currentPath === '/my-videos' ? "text-indigo-400 drop-shadow-[0_0_10px_rgba(129,140,248,0.5)]" : "text-slate-300 hover:text-white"
                    )}
                  >
                    My Videos
                  </button>
                  <button 
                    onClick={() => { setShowAbout(false); navigateTo('/youtube'); }}
                    className={cn(
                      "font-bold transition-all text-base",
                      currentPath === '/youtube' ? "text-indigo-400 drop-shadow-[0_0_10px_rgba(129,140,248,0.5)]" : "text-slate-300 hover:text-white"
                    )}
                  >
                    YouTube
                  </button>
                  <button 
                    onClick={() => setShowAbout(true)}
                    className={cn(
                      "font-bold transition-all text-base",
                      showAbout ? "text-indigo-400 drop-shadow-[0_0_10px_rgba(129,140,248,0.5)]" : "text-slate-300 hover:text-white"
                    )}
                  >
                    About Us
                  </button>
                </div>

                <div className="flex items-center gap-6">
                  {/* Google Login Button */}
                  <div className="flex flex-col items-center gap-1.5">
                    <button 
                      onClick={userEmail ? logoutFromGoogle : () => setIsAuthInstructionsModalOpen(true)}
                      className="px-6 py-2.5 rounded-xl text-sm font-bold transition-all h-[44px] shadow-md flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white"
                    >
                      {userEmail ? (
                         <>
                           <div className="w-5 h-5 rounded-full overflow-hidden bg-white/20 flex items-center justify-center text-[10px]">
                             {userEmail.charAt(0).toUpperCase()}
                           </div>
                           Logout
                         </>
                      ) : (
                        <>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
                          </svg>
                          Login with Google
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex flex-col items-center gap-1.5">
                    <button 
                      onClick={() => setIsApiKeyModalOpen(true)}
                      className={cn(
                        "px-6 py-2.5 rounded-xl text-sm font-bold transition-all h-[44px] shadow-md",
                        localStorage.getItem('gemini_api_key') 
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200" 
                          : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-200"
                      )}
                    >
                      {localStorage.getItem('gemini_api_key') ? 'API Key Active' : 'Set Your API Key'}
                    </button>
                    <div className="hidden xl:block text-[11px] text-zinc-400 text-center leading-tight font-medium drop-shadow-sm">
                      For Unlimited use, <br />
                      set your own API key
                    </div>
                  </div>
                  <QuotaCounter userEmail={userEmail} />
                </div>
              </div>
            </header>
          )}

          <main className={cn("relative mx-auto transition-all duration-500", aiData ? "w-full px-0 pt-0 pb-0" : "max-w-[1700px] px-14 sm:px-16 pt-0 pb-12")}>
            {currentPath === '/privacy' ? (
              <PrivacyPolicy onBack={() => navigateTo('/')} />
            ) : currentPath === '/terms' ? (
              <TermsOfService onBack={() => navigateTo('/')} />
            ) : currentPath === '/topics' ? (
              <TopicsPage onNavigate={navigateTo} />
            ) : currentPath.startsWith('/explore/') ? (
              <ExplorePage onNavigate={navigateTo} path={currentPath} />
            ) : currentPath.startsWith('/blog') ? (
              <BlogPage onNavigate={navigateTo} slug={currentPath.split('/')[2]} />
            ) : currentPath.startsWith('/tools') ? (
              <ToolsPage onNavigate={navigateTo} />
            ) : currentPath.startsWith('/question/') ? (
              <QuestionPage slug={currentPath.split('/')[2]} onNavigate={navigateTo} />
            ) : currentPath === '/my-videos' ? (
              <div className="min-h-screen relative max-w-[1700px] mx-auto px-6 py-12 text-slate-200">
                <button 
                  onClick={() => navigateTo('/')}
                  className="group text-slate-400 hover:text-white flex items-center gap-2 font-medium mb-12 transition-colors relative z-10 cursor-pointer"
                >
                  <div className="p-2 rounded-full bg-slate-800/50 group-hover:bg-slate-800 transition-colors border border-white/5">
                    <ArrowLeft className="w-4 h-4" />
                  </div>
                  Back to Home
                </button>
                <MyVideos 
                  userEmail={userEmail} 
                  onReplayVideo={handleReplayVideo}
                  onLoginClick={() => setIsAuthInstructionsModalOpen(true)}
                />
              </div>
            ) : currentPath === '/youtube' ? (
              <div className="min-h-screen relative max-w-[1700px] mx-auto px-6 py-12 text-slate-200">
                <button 
                  onClick={() => navigateTo('/')}
                  className="group text-slate-400 hover:text-white flex items-center gap-2 font-medium mb-12 transition-colors relative z-10 cursor-pointer"
                >
                  <div className="p-2 rounded-full bg-slate-800/50 group-hover:bg-slate-800 transition-colors border border-white/5">
                    <ArrowLeft className="w-4 h-4" />
                  </div>
                  Back to Home
                </button>
                <YouTubeHub />
              </div>
            ) : showAbout ? (
              <AboutPage onBack={() => setShowAbout(false)} />
            ) : (
              <>
            {/* Hero Section */}
            {!aiData && (
              <div id="hero-section" className="text-center max-w-6xl mx-auto mb-1 pt-0 min-h-[160px] flex flex-col items-center justify-center relative z-10">
                <AnimatePresence mode="wait">
                  {showIntro ? (
                    <motion.div
                      key="intro"
                      initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
                      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                      exit={{ opacity: 0, scale: 1.1, filter: 'blur(20px)' }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className="flex flex-col items-center"
                    >
                      <h2 className="text-9xl font-black text-white tracking-tighter drop-shadow-[0_0_30px_rgba(168,85,247,0.5)] uppercase italic">
                        Professor X
                      </h2>
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: '100%' }}
                        transition={{ delay: 0.5, duration: 2 }}
                        className="h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent mt-4"
                      />
                    </motion.div>
                  ) : !isLoading ? (
                    <motion.div
                      key={activeMode}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -15, filter: "blur(10px)" }}
                      transition={{ duration: 0.4 }}
                      className="w-full text-center"
                    >
                      <h2 className="text-6xl lg:text-7xl font-extrabold text-white tracking-tight mb-3 leading-tight drop-shadow-2xl text-center">
                        {activeMode === 'oneshot' ? (
                          <>
                            Turn Your Syllabus Into <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-green-400 to-teal-300 block mt-1">
                              Modulewise Visual Learning
                            </span>
                          </>
                        ) : (
                          <>
                            Transform Doubts Into <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 to-purple-400 block mt-1">
                              Stepwise Visual AI Explanations
                            </span>
                          </>
                        )}
                      </h2>
                      <p className="text-xl lg:text-2xl text-slate-300 mb-6 leading-relaxed max-w-5xl mx-auto drop-shadow-md text-center">
                        {activeMode === 'oneshot'
                          ? "Turn your syllabus, chapters, and concepts into structured AI-powered lessons with step-by-step explanations, visuals, examples, and exam-focused learning."
                          : "Professor X AI is your personalized AI tutor that explains concepts visually through multilingual teaching, animated solutions, smart notes, and real-time AI-powered learning."
                        }
                      </p>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            )}

            {/* Main Content Area */}
            <div className="space-y-6">
              {!aiData ? (
                !showIntro && (
                  <>
                    <AnimatePresence mode="wait">
                      {!isLoading ? (
                        <motion.div
                          id="input-section"
                          key="input-section"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, filter: "blur(10px)" }}
                          transition={{ duration: 1 }}
                          className="relative z-[70] mt-2 px-4 w-full max-w-[1500px] mx-auto flex flex-col items-center justify-center gap-5"
                        >
                          {/* Mode Switcher Tabs */}
                          <div className="flex items-center justify-center gap-3 p-1.5 bg-slate-900/90 backdrop-blur-md rounded-2xl border border-white/10 shadow-xl">
                            <button
                              type="button"
                              onClick={() => setActiveMode('doubt')}
                              className={cn(
                                "flex items-center gap-2.5 px-6 py-2.5 rounded-xl font-montserrat font-bold text-xs sm:text-sm transition-all cursor-pointer border shadow-sm",
                                activeMode === 'doubt'
                                  ? "bg-indigo-600 text-white border-indigo-400 shadow-indigo-500/25"
                                  : "bg-transparent text-slate-400 border-transparent hover:text-white hover:bg-white/5"
                              )}
                            >
                              <HelpCircle className="w-4 h-4 text-indigo-300" />
                              <span>AI Doubt Solver</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => setActiveMode('oneshot')}
                              className={cn(
                                "flex items-center gap-2.5 px-6 py-2.5 rounded-xl font-montserrat font-bold text-xs sm:text-sm transition-all cursor-pointer border shadow-sm",
                                activeMode === 'oneshot'
                                  ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white border-purple-400 shadow-purple-500/25"
                                  : "bg-transparent text-slate-400 border-transparent hover:text-white hover:bg-white/5"
                              )}
                            >
                              <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                              <span>OneShot Learning</span>
                              <span className="bg-amber-400/20 text-amber-300 border border-amber-400/30 text-[10px] uppercase font-extrabold px-2 py-0.5 rounded-full ml-1">
                                NEW
                              </span>
                            </button>
                          </div>

                          <div className="w-full flex-grow max-w-4xl flex justify-center">
                            {activeMode === 'doubt' ? (
                              <InputSection onSubmit={handleGenerate} isLoading={isLoading} />
                            ) : (
                              <OneShotInputSection onSubmit={handleOneShotSubmit} isLoading={isLoading} />
                            )}
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="loading-cinematic"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 1.5 }}
                          className="relative z-[60] -mt-24 flex flex-col items-center justify-center min-h-[900px] w-full"
                        >
                          {/* Center Top Statement */}
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1.2, delay: 0.5 }}
                            className="absolute top-2 text-center z-10 w-full"
                          >
                            <div className="relative inline-block px-10 py-4 group">
                              {/* Ambient Background Glow */}
                              <motion.div 
                                animate={{ 
                                  opacity: [0.1, 0.2, 0.1],
                                  scale: [0.95, 1.05, 0.95]
                                }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute inset-0 bg-indigo-500/10 blur-[50px] -z-10 rounded-full"
                              />
                              
                              {/* Shimmer Border Light Effect */}
                              <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" />
                              <motion.div 
                                animate={{ x: ["-100%", "200%"] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }}
                                className="absolute inset-x-0 bottom-0 h-[1px] w-1/2 bg-gradient-to-r from-transparent via-white to-transparent"
                              />

                              <h3 className="text-4xl sm:text-5xl font-black text-white tracking-widest uppercase italic drop-shadow-[0_0_30px_rgba(255,255,255,0.3)] relative">
                                LEARN LIMITLESS <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 drop-shadow-[0_0_15px_rgba(129,140,248,0.6)]">WITH AI</span>
                              </h3>
                            </div>
                          </motion.div>

                          {/* Side Statements */}
                          <div className="absolute inset-0 flex items-start justify-between px-8 sm:px-16 pointer-events-none pt-32 pb-32">
                            {/* Left Side Statement */}
                            <motion.div
                              initial={{ opacity: 0, x: -50 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 1.2, delay: 0.8 }}
                              className="max-w-[280px] sm:max-w-md lg:max-w-lg"
                            >
                              <h4 className="text-2xl sm:text-4xl font-black text-white leading-tight tracking-tight drop-shadow-[0_5px_15px_rgba(0,0,0,0.5)]">
                                Your <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 via-purple-300 to-white">personalized AI</span> <br />
                                tutoring assistant
                              </h4>
                              <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-indigo-500 to-transparent mt-4 rounded-full shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
                            </motion.div>

                            {/* Right Side Statement */}
                            <motion.div
                              initial={{ opacity: 0, x: 50 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 1.2, delay: 1.0 }}
                              className="max-w-[280px] sm:max-w-md lg:max-w-lg text-right"
                            >
                              <h4 className="text-2xl sm:text-4xl font-black text-white leading-tight tracking-tight drop-shadow-[0_5px_15px_rgba(0,0,0,0.5)]">
                                Transforming doubts <br />
                                <span className="bg-clip-text text-transparent bg-gradient-to-l from-indigo-300 via-purple-300 to-white">into visual concepts</span>
                              </h4>
                              <div className="h-1 w-16 sm:w-24 bg-gradient-to-l from-indigo-500 to-transparent mt-4 ml-auto rounded-full shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
                            </motion.div>
                          </div>

                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1.5, delay: 0.2 }}
                            className="mb-6 relative mt-16"
                          >
                            <div className="absolute -inset-6 bg-indigo-500/20 blur-3xl rounded-full animate-pulse" />
                            <img 
                              src="/profxlogo.png" 
                              alt="Professor X Logo" 
                              className="w-24 h-24 sm:w-32 sm:h-32 object-contain relative z-10"
                            />
                          </motion.div>

                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 0.4 }}
                            className="text-xl sm:text-2xl text-amber-500 mb-6 font-bold tracking-tight text-center drop-shadow-[0_0_10px_rgba(245,158,11,0.3)] px-6"
                          >
                            Wait Patiently, till Professor X understands your question..
                          </motion.p>

                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1.5, delay: 0.3 }}
                            className="w-full max-w-4xl aspect-video rounded-[2rem] sm:rounded-[3rem] overflow-hidden relative shadow-[0_0_100px_rgba(79,70,229,0.3)] border border-white/10 mx-6 bg-[#05050a] flex items-center justify-center"
                          >
                            <TransitionVideo videoSrc="/transai.mp4" />
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Error Message - Now below InputSection */}
                    <AnimatePresence>
                      {error && (
                        <motion.div 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="max-w-3xl mx-auto mt-8 bg-red-900/30 border border-red-800 text-red-400 px-6 py-4 rounded-xl flex items-center gap-3"
                        >
                          <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                          <p className="text-sm font-medium">{cleanAuthErrorMessage(error)}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-8"
                >
                  <ErrorBoundary>
                    <div id="video-section" className="w-full overflow-hidden">
                      <VideoPlayer 
                        data={aiData} 
                        doubtData={doubtData}
                        language={selectedLanguage} 
                        onReset={() => {
                          setAiData(null);
                          setDoubtData(null);
                        }}
                        onVideoEnd={() => scrollToSection('results-details')}
                      />
                    </div>
                    <div id="results-details" className="flex flex-row gap-6 w-full items-start px-12 mt-12">
                      <div className="flex-1 min-w-0">
                        <NotesSection data={aiData} />
                      </div>
                      <div className="w-[500px] flex-shrink-0">
                        <ChatSection 
                          aiData={aiData} 
                          language={selectedLanguage} 
                        />
                      </div>
                    </div>
                  </ErrorBoundary>
                </motion.div>
              )}
            </div>

            {/* Features Grid (Only show when no data) */}
            {!aiData && !isLoading && (
              <div id="features-section" className="mt-6 flex flex-col items-center">
                <div className="grid sm:grid-cols-3 gap-5 max-w-6xl mx-auto w-full">
                  <div className="bg-slate-900/50 backdrop-blur-sm px-5 py-3.5 rounded-2xl border border-white/10 shadow-xl flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-1.5">
                      <div className="w-10 h-10 bg-indigo-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Youtube className="w-5 h-5 text-indigo-400" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-100">AI Video Explanation</h3>
                    </div>
                    <p className="text-xs lg:text-[13px] text-slate-400 leading-normal whitespace-normal lg:whitespace-nowrap truncate">Instantly creates animated AI professor explanations with step-by-step guidance.</p>
                  </div>
                  <div className="bg-slate-900/50 backdrop-blur-sm px-5 py-3.5 rounded-2xl border border-white/10 shadow-xl flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-1.5">
                      <div className="w-10 h-10 bg-emerald-900/30 rounded-xl flex-shrink-0 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-emerald-400" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-100">Smart Study Notes</h3>
                    </div>
                    <p className="text-xs lg:text-[13px] text-slate-400 leading-normal whitespace-normal lg:whitespace-nowrap truncate">Generates real-time formula sheets, diagram summaries, and topic breakdowns.</p>
                  </div>
                  <div className="bg-slate-900/50 backdrop-blur-sm px-5 py-3.5 rounded-2xl border border-white/10 shadow-xl flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-1.5">
                      <div className="w-10 h-10 bg-amber-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-5 h-5 text-amber-400" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-100">Adaptive Learning</h3>
                    </div>
                    <p className="text-xs lg:text-[13px] text-slate-400 leading-normal whitespace-normal lg:whitespace-nowrap truncate">Personalized explanations that adapt to your language preference and learning pace.</p>
                  </div>
                </div>

                <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row justify-center items-center gap-8 sm:gap-16 lg:gap-24 text-white">
                  <div className="flex flex-col items-center text-center">
                    <span className="text-3xl sm:text-4xl font-black">21+</span>
                    <span className="text-base sm:text-xl font-bold mt-1 text-purple-300">Languages Supported</span>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <span className="text-3xl sm:text-4xl font-black">1 Million+</span>
                    <span className="text-base sm:text-xl font-bold mt-1 text-purple-300">Topics &amp; Questions</span>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <span className="text-3xl sm:text-4xl font-black">100+</span>
                    <span className="text-base sm:text-xl font-bold mt-1 text-purple-300">Subjects Covered</span>
                  </div>
                </div>

                {/* SEO SECTIONS */}
                <div className="mt-28 max-w-5xl mx-auto w-full px-6 flex flex-col gap-20">
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full -mb-8">
                    <button 
                      onClick={() => scrollToSection('input-section')}
                      className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black text-xl shadow-[0_0_20px_rgba(79,70,229,0.4)] transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                    >
                      <GraduationCap className="w-6 h-6" /> Start Learning
                    </button>
                    <button 
                      onClick={() => scrollToSection('input-section')}
                      className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-2xl font-black text-xl backdrop-blur-md transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                    >
                      <Sparkles className="w-6 h-6 text-amber-400" /> Generate AI Solution
                    </button>
                  </div>

                  {/* Why Professor X AI */}
                  <div className="bg-gradient-to-br from-slate-900/80 to-indigo-950/30 backdrop-blur-md border border-indigo-500/20 rounded-3xl p-8 sm:p-12 shadow-[0_0_50px_rgba(79,70,229,0.1)] relative overflow-hidden group hover:border-indigo-500/40 transition-all duration-500">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/3 group-hover:bg-indigo-500/20 transition-colors duration-700"></div>
                    <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-[80px] pointer-events-none translate-y-1/2 -translate-x-1/3 group-hover:bg-purple-500/20 transition-colors duration-700"></div>
                    
                    <div className="relative z-10 flex flex-col md:flex-row gap-10 items-center">
                      <div className="flex-1 space-y-6">
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-indigo-500/30 p-1 bg-indigo-500/10 mb-4 shadow-[0_0_15px_rgba(79,70,229,0.2)]">
                          <img src="/profxlogo.png" alt="Professor X AI" className="w-full h-full rounded-full object-cover bg-black" />
                        </div>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-sm font-semibold tracking-wide shadow-[0_0_15px_rgba(79,70,229,0.2)]">
                          <Sparkles className="w-4 h-4" /> Next-Generation Education
                        </div>
                        <h2 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-indigo-200 leading-tight">
                          Why Students Choose Professor X AI
                        </h2>
                      </div>
                      
                      <div className="flex-1 space-y-6 text-slate-300 leading-relaxed text-lg border-l border-white/10 md:pl-10 relative">
                        <div className="absolute left-0 top-0 w-[1px] h-full bg-gradient-to-b from-indigo-500/0 via-indigo-500/50 to-indigo-500/0 md:block hidden"></div>
                        <ul className="space-y-5">
                          <li className="flex items-start gap-4">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <CheckCircle2 className="w-5 h-5 text-indigo-400" />
                            </div>
                            <div>
                              <strong className="text-white/90 text-xl block mb-1">Interactive AI Learning</strong>
                              <p className="text-slate-400 text-base leading-relaxed">Transforms complex conceptual doubts into deeply engaging step-by-step interactive sessions rather than just providing static answers.</p>
                            </div>
                          </li>
                          <li className="flex items-start gap-4">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <CheckCircle2 className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                              <strong className="text-white/90 text-xl block mb-1">Universal Exam Support</strong>
                              <p className="text-slate-400 text-base leading-relaxed">Comprehensive learning support spanning across JEE, NEET, CAT, Engineering Maths, coding mechanics, and standard academics.</p>
                            </div>
                          </li>
                          <li className="flex items-start gap-4">
                            <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            </div>
                            <div>
                              <strong className="text-white/90 text-xl block mb-1">Visual & Auditory Integration</strong>
                              <p className="text-slate-400 text-base leading-relaxed">Multilingual teaching with animated visual explanations to construct a personalized, rich educational experience tailored to your pace.</p>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Specialized Learning Modes: Doubt Solving & OneShot Learning */}
                  <div className="space-y-6">
                    <div className="text-center space-y-2">
                      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-bold uppercase tracking-widest shadow-[0_0_15px_rgba(79,70,229,0.15)]">
                        <Sparkles className="w-3.5 h-3.5" /> Core Learning Pillars
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                        Choose Your AI Learning Experience
                      </h3>
                      <p className="text-slate-400 text-sm max-w-2xl mx-auto">
                        Whether you need fast clarity on an individual doubt or a full chapter masterclass, Professor X provides tailored AI teaching engines.
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Doubt Solving Mode Briefing */}
                      <div className="bg-gradient-to-br from-indigo-950/40 via-slate-900/60 to-slate-950 border border-indigo-500/20 hover:border-indigo-500/40 rounded-3xl p-8 sm:p-10 relative overflow-hidden group transition-all duration-500 shadow-2xl flex flex-col justify-between">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px] pointer-events-none group-hover:bg-indigo-500/20 transition-all duration-500"></div>
                        <div>
                          <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center mb-6 text-indigo-400 group-hover:scale-110 transition-transform">
                            <HelpCircle className="w-7 h-7" />
                          </div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-bold uppercase tracking-wider mb-3">
                            Instant Doubt Resolution
                          </div>
                          <h3 className="text-2xl sm:text-3xl font-black text-white mb-4 tracking-tight">
                            Doubt Solving Mode
                          </h3>
                          <p className="text-slate-300 leading-relaxed mb-6">
                            Engineered for quick, precise, step-by-step resolution of individual conceptual questions, numerical problems, and exam questions with instant visual chalkboard teaching.
                          </p>
                          <ul className="space-y-3 text-slate-300 text-sm">
                            <li className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                              <span><strong>Multi-Modal Input:</strong> Submit doubts via text, voice, handwritten problem notes, or image attachments</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                              <span><strong>Visual Chalkboard Teaching:</strong> Step-by-step blackboard writing with LaTeX formulas & math tables</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                              <span><strong>Multilingual Voice Narration:</strong> Natural voice teaching in 16+ languages with word-by-word caption sync</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                              <span><strong>Interactive Doubt Chat:</strong> Ask infinite follow-up questions to clarify intermediate calculation steps</span>
                            </li>
                          </ul>
                        </div>
                        <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
                          <span className="text-xs font-bold uppercase tracking-widest text-indigo-400">Best for: Daily Homework & Numerical Doubts</span>
                          <Sparkles className="w-4 h-4 text-indigo-400" />
                        </div>
                      </div>

                      {/* OneShot Learning Mode Briefing */}
                      <div className="bg-gradient-to-br from-purple-950/40 via-slate-900/60 to-slate-950 border border-purple-500/20 hover:border-purple-500/40 rounded-3xl p-8 sm:p-10 relative overflow-hidden group transition-all duration-500 shadow-2xl flex flex-col justify-between">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] pointer-events-none group-hover:bg-purple-500/20 transition-all duration-500"></div>
                        <div>
                          <div className="w-14 h-14 rounded-2xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center mb-6 text-purple-400 group-hover:scale-110 transition-transform">
                            <BookOpen className="w-7 h-7" />
                          </div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-wider mb-3">
                            Masterclass Topic Coverage
                          </div>
                          <h3 className="text-2xl sm:text-3xl font-black text-white mb-4 tracking-tight">
                            OneShot Learning Mode
                          </h3>
                          <p className="text-slate-300 leading-relaxed mb-6">
                            A comprehensive 15–20+ minute deep masterclass system that builds complete chapter mastery from topic queries or uploaded syllabus & textbook PDFs.
                          </p>
                          <ul className="space-y-3 text-slate-300 text-sm">
                            <li className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                              <span><strong>7-Module Master Roadmap:</strong> Complete structured journey from core foundations to exam applications</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                              <span><strong>Syllabus PDF Analysis:</strong> Upload textbook chapters, class notes, or syllabus files for targeted learning</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                              <span><strong>Deep Concept Progression:</strong> Definitions, formulas, step-by-step derivations, worked examples, and demos</span>
                            </li>
                            <li className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                              <span><strong>Exam Q&A & Revision:</strong> Practice questions, key takeaways, and comprehensive revision for competitive exams</span>
                            </li>
                          </ul>
                        </div>
                        <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
                          <span className="text-xs font-bold uppercase tracking-widest text-purple-400">Best for: Chapter Revisions & Exam Preparation</span>
                          <Sparkles className="w-4 h-4 text-purple-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-slate-900/50 backdrop-blur-md border border-white/10 rounded-3xl p-8 sm:p-10 relative overflow-hidden group hover:border-purple-500/30 transition-colors duration-500 shadow-xl">
                      <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full blur-[60px] translate-y-1/3 -translate-x-1/3 group-hover:bg-purple-500/20 transition-colors duration-500"></div>
                      <h2 className="text-2xl font-black text-white mb-6 relative z-10 flex items-center gap-3">
                        <Settings2 className="w-6 h-6 text-purple-400" />
                        AI-Powered Features
                      </h2>
                      <ul className="space-y-3 text-slate-300 relative z-10">
                        {['Visual AI explanations', 'Multilingual teaching support', 'Animated step-by-step solutions', 'AI-generated notes', 'Diagram-based learning', 'Real-time concept visualization', 'Smart mathematical narration', 'Interactive educational experience', 'Personalized AI tutoring', 'Exam-focused concept clarity'].map((feature, i) => (
                          <li key={i} className="flex items-start gap-3 group/item">
                            <div className="w-2 h-2 rounded-full bg-purple-500/50 group-hover/item:bg-purple-400 mt-2.5 flex-shrink-0 transition-colors" />
                            <span className="group-hover/item:text-slate-200 transition-colors">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-slate-900/50 backdrop-blur-md border border-white/10 rounded-3xl p-8 sm:p-10 relative overflow-hidden group hover:border-emerald-500/30 transition-colors duration-500 shadow-xl">
                       <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-[60px] -translate-y-1/3 translate-x-1/3 group-hover:bg-emerald-500/20 transition-colors duration-500"></div>
                       <h2 className="text-2xl font-black text-white mb-6 relative z-10 flex items-center gap-3">
                         <GraduationCap className="w-6 h-6 text-emerald-400" />
                         Built For Every Competitive Exam
                       </h2>
                       <p className="text-slate-300 mb-6 relative z-10">Professor X AI supports learning for:</p>
                       <ul className="space-y-3 text-slate-300 relative z-10">
                         {['JEE Main & Advanced', 'NEET', 'CAT', 'UPSC', 'GATE', 'Engineering students', 'School students', 'Coding and technical subjects'].map((exam, i) => (
                           <li key={i} className="flex items-center gap-3 group/item">
                             <div className="w-2 h-2 rounded-full bg-emerald-500/50 group-hover/item:bg-emerald-400 flex-shrink-0 transition-colors" />
                             <span className="group-hover/item:text-slate-200 transition-colors">{exam}</span>
                           </li>
                         ))}
                       </ul>
                    </div>
                  </div>

                  {/* Experience The Future Of Learning */}
                  <div className="text-center max-w-4xl mx-auto relative px-4 py-8">
                     <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-[600px] bg-indigo-500/5 rounded-full blur-[100px] pointer-events-none"></div>
                     <h2 className="text-3xl sm:text-4xl font-black text-white mb-6 relative z-10">Experience The Future Of Learning</h2>
                     <p className="text-xl text-slate-300 leading-relaxed relative z-10">
                       Professor X AI uses advanced artificial intelligence to generate explanation videos, voice narration, diagrams, visual representations, and smart notes instantly from any question. The platform creates a highly interactive and immersive learning environment designed for conceptual understanding rather than rote memorization.
                     </p>
                  </div>

                  {/* FAQ Section */}
                  <div className="bg-slate-900/50 backdrop-blur-md border border-white/10 rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden group hover:border-indigo-500/20 transition-colors duration-500">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none"></div>
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-[100px] pointer-events-none"></div>
                    
                    <h2 className="text-3xl font-black text-white mb-10 text-center relative z-10">Frequently Asked Questions</h2>
                    <div className="space-y-6 relative z-10">
                      {[
                        { q: "What is Professor X AI?", a: "Professor X AI is an AI-powered visual tutoring platform that converts doubts into animated step-by-step explanations with notes, multilingual narration, and visual learning." },
                        { q: "Which exams does Professor X AI support?", a: "Professor X AI supports JEE, NEET, CAT, UPSC, Engineering, school education, and technical subjects." },
                        { q: "Does Professor X AI support multilingual learning?", a: "Yes, Professor X AI supports multilingual explanations and narration for better conceptual understanding." },
                        { q: "Can Professor X AI generate notes and diagrams?", a: "Yes, the platform automatically generates AI-powered notes, diagrams, and visual explanations for learning." }
                      ].map((faq, i) => (
                        <div key={i} className="bg-white/5 border border-white/5 rounded-2xl p-6 hover:bg-white/10 hover:border-white/10 transition-all duration-300">
                          <h3 className="text-lg sm:text-xl font-bold text-slate-100 mb-3 flex items-start gap-3">
                            <span className="text-indigo-400 mt-1"><Sparkles className="w-5 h-5" /></span>
                            {faq.q}
                          </h3>
                          <p className="text-slate-400 pl-8 leading-relaxed">{faq.a}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-16 flex justify-center relative z-10">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-indigo-500/30 p-1 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 shadow-[0_0_30px_rgba(79,70,229,0.2)]">
                    <img src="/profxlogo.png" alt="Professor X AI" className="w-full h-full rounded-full object-cover bg-black" />
                  </div>
                </div>

                <div className="mt-20 pt-8 border-t border-white/5 flex flex-col items-center gap-4">
                  <div className="flex items-center gap-8 text-slate-500 text-sm font-medium">
                    <button onClick={() => setShowAbout(true)} className="hover:text-indigo-400 cursor-pointer transition-colors">About Us</button>
                    <a href="/topics" onClick={(e) => { e.preventDefault(); navigateTo('/topics'); }} className="hover:text-amber-400 cursor-pointer transition-colors font-semibold text-slate-400">Browse Topics</a>
                    <a href="/privacy" onClick={(e) => { e.preventDefault(); navigateTo('/privacy'); }} className="hover:text-indigo-400 cursor-pointer transition-colors">Privacy Policy</a>
                    <a href="/terms" onClick={(e) => { e.preventDefault(); navigateTo('/terms'); }} className="hover:text-indigo-400 cursor-pointer transition-colors">Terms of Service</a>
                    <button onClick={handleContactClick} className="hover:text-indigo-400 cursor-pointer transition-colors">Contact Support</button>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-slate-600 text-xs">
                      &copy; 2026 Professor X AI. Empowering knowledge through algorithmic visualization.
                    </p>
                    <div className="flex gap-4 text-[10px] text-slate-700 uppercase tracking-widest font-black italic">
                      <span>Security: TLS 1.3</span>
                      <span>•</span>
                      <span>Privacy: GPA Compliant</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </>
            )}
          </main>
      </div>

      {aiData && (
        <button 
          onClick={() => scrollToSection('results-details')}
          className="fixed bottom-8 right-8 z-[10000] bg-indigo-600 p-5 rounded-full border border-white/20 text-white hover:bg-indigo-500 transition-all shadow-xl hover:scale-110 active:scale-95 group"
          title="Jump to Study Notes & Doubt Chat"
        >
          <HelpCircle className="w-8 h-8" />
          <span className="absolute right-full mr-4 px-3 py-1 bg-slate-900 border border-white/10 rounded-lg text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Help & Notes
          </span>
        </button>
      )}

      {/* API Key Modal */}
        <AnimatePresence>
          {isApiKeyModalOpen && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsApiKeyModalOpen(false)}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              />
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="relative bg-slate-900 border border-slate-800 rounded-3xl p-8 w-full max-w-md shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-indigo-600/20 p-2 rounded-xl">
                      <Settings2 className="w-5 h-5 text-indigo-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Set Gemini API Key</h3>
                  </div>
                  <button 
                    onClick={() => setIsApiKeyModalOpen(false)}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors text-slate-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                  Enter your personal Google Gemini API key to avoid quota limits. Your key is stored locally in your browser and never sent to our servers.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 ml-1">
                      Gemini API Key
                    </label>
                    <input
                      type="password"
                      value={userApiKey}
                      onChange={(e) => setUserApiKey(e.target.value)}
                      placeholder="AIzaSy..."
                      className="w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 rounded-xl px-4 py-3 outline-none transition-all text-slate-100 placeholder:text-slate-700"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        localStorage.removeItem('gemini_api_key');
                        setUserApiKey('');
                        setIsApiKeyModalOpen(false);
                      }}
                      className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-bold transition-all text-sm"
                    >
                      Clear Key
                    </button>
                    <button
                      onClick={() => {
                        localStorage.setItem('gemini_api_key', userApiKey.trim());
                        setIsApiKeyModalOpen(false);
                        window.location.reload(); // Reload to ensure new key is used everywhere
                      }}
                      className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all text-sm shadow-lg shadow-indigo-500/20"
                    >
                      Save Key
                    </button>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-800">
                  <a 
                    href="https://aistudio.google.com/app/apikey" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300 text-xs font-bold flex items-center justify-center gap-2 transition-colors"
                  >
                    Get a free API key from Google AI Studio
                    <Youtube className="w-3 h-3" />
                  </a>
                </div>
              </motion.div>
            </div>
          )}

          {isAuthInstructionsModalOpen && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsAuthInstructionsModalOpen(false)}
                className="absolute inset-0 bg-black/85 backdrop-blur-md"
              />
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 15 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 15 }}
                className="relative bg-[#0d131f] border border-slate-800 rounded-3xl p-7 md:p-8 w-full max-w-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3.5">
                    <div className="w-11 h-11 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                      <ShieldAlert className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white tracking-tight">Google Sign-in Required</h3>
                      <p className="text-xs font-black text-amber-400 tracking-wider uppercase mt-0.5">COMPULSORY ACTION REQUIRED</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsAuthInstructionsModalOpen(false)}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors text-slate-400 -mr-1 -mt-1"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <p className="text-slate-300 text-sm leading-relaxed">
                    To enable Professor X to generate cinematic AI explanations, diagram illustrations, and study notes on your browser, you must authorize full permissions during Google Sign-in.
                  </p>

                  <div className="bg-[#1c1614] border border-amber-900/40 rounded-2xl p-5 space-y-3.5 shadow-inner">
                    <div className="flex items-center gap-2 text-amber-400 font-black text-xs uppercase tracking-wider">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>CRITICAL SIGN-IN REQUIREMENT</span>
                    </div>

                    <div className="space-y-3 text-sm text-slate-200">
                      <div className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          1
                        </span>
                        <p className="leading-snug">
                          A popup window will open for you to choose or sign in to your Google Account.
                        </p>
                      </div>

                      <div className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          2
                        </span>
                        <p className="leading-snug">
                          On the permission screen, you{' '}
                          <span className="text-amber-300 font-bold underline decoration-amber-400 underline-offset-2">
                            MUST TICK / CHECK the &quot;Select all&quot; checkbox
                          </span>{' '}
                          (or check each individual scope).
                        </p>
                      </div>

                      <div className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          3
                        </span>
                        <p className="leading-snug">
                          Click <strong className="text-white font-bold">&quot;Continue&quot;</strong> to complete the login. Skipping any checkbox will cause authentication errors.
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-400 italic leading-relaxed pt-1">
                    Note: Your session token is strictly stored locally in your browser. If you forgot to tick the options, please log out and sign in again to resolve.
                  </p>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setIsAuthInstructionsModalOpen(false)}
                      className="flex-1 py-3 px-4 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl font-bold transition-all text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProceedGoogleLogin}
                      className="flex-1 py-3 px-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all text-sm shadow-lg shadow-indigo-600/30 active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
                      </svg>
                      Proceed to Sign In
                    </button>
                  </div>

                  <p id="login-terms-notice" className="text-center text-xs text-slate-400 pt-1 leading-relaxed">
                    By continuing, you agree to the{' '}
                    <a
                      id="login-terms-link"
                      href="/terms"
                      onClick={(e) => {
                        if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
                          e.preventDefault();
                          setIsAuthInstructionsModalOpen(false);
                          navigateTo('/terms');
                        }
                      }}
                      className="text-indigo-400 hover:text-indigo-300 underline font-medium cursor-pointer transition-colors"
                    >
                      Terms &amp; Conditions
                    </a>{' '}
                    and{' '}
                    <a
                      id="login-privacy-link"
                      href="/privacy"
                      onClick={(e) => {
                        if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
                          e.preventDefault();
                          setIsAuthInstructionsModalOpen(false);
                          navigateTo('/privacy');
                        }
                      }}
                      className="text-indigo-400 hover:text-indigo-300 underline font-medium cursor-pointer transition-colors"
                    >
                      Privacy Policy
                    </a>.
                  </p>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
